import { createTheme, ThemeProvider as Provider } from '@mui/material';

import Roboto from '../fonts/Roboto.woff2';

import { overRideTypography } from './overRideTypography';
import { overRideComponents } from './overRideComponents';

declare module '@mui/material/styles' {
  interface Palette {
    green: Palette['primary'] & {
      ['main']: string;
    };
    blue: Palette['primary'] & {
      ['main']: string;
    };
    orange: Palette['primary'] & {
      ['main']: string;
    };
    red: Palette['primary'] & {
      ['main']: string;
    };
    gray: PaletteOptions['primary'] & {
      ['main']: string;
      ['50']: string;
      ['100']: string;
      ['150']: string;
      ['200']: string;
      ['300']: string;
    };
  }

  interface PaletteOptions {
    green?: PaletteOptions['primary'] & {
      ['main']: string;
    };
    blue?: PaletteOptions['primary'] & {
      ['main']: string;
    };
    orange?: PaletteOptions['primary'] & {
      ['main']: string;
    };
    red?: PaletteOptions['primary'] & {
      ['main']: string;
    };
    gray?: PaletteOptions['primary'] & {
      ['main']: string;
      ['50']: string;
      ['100']: string;
      ['150']: string;
      ['200']: string;
      ['300']: string;
    };
  }
}

export const theme = createTheme({
  ...createTheme(),
  palette: {
    // The app is dark-themed; without this CssBaseline paints <body> with
    // MUI's light default (#fafafa), which shows through as a white block
    // when the mobile keyboard opens and the dark content no longer covers
    // the full viewport.
    background: {
      default: '#272727',
    },
    green: {
      main: '#24EDA4',
    },
    blue: {
      main: '#1DAEEF',
    },
    orange: {
      main: '#EF9E1D',
    },
    red: {
      main: '#EB5F5F',
    },
    gray: {
      main: '#E8E8E8',
      50: '#8A8A8E',
      100: '#C7C7C7',
      150: '#3D3D3D',
      200: '#373737',
      300: '#272727',
    },
    common: {
      black: '#171717',
    },
    error: {
      main: '#EB5F5F',
    },
    success: {
      main: '#27DB87',
    },
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 360,
      md: 700,
      lg: 1170,
      xl: 2560,
    },
  },
});

theme.typography = {
  ...overRideTypography(theme),
};

theme.components = {
  ...createTheme,
  MuiCssBaseline: {
    styleOverrides: `
        @font-face {
          font-family: 'Roboto';
          font-style: normal;
          font-display: swap;
          src: local('Roboto'), local('Roboto'), url(${Roboto}) format('woff2');
        }
      `,
  },
  ...overRideComponents(theme),
};

export const ThemeProvider = ({ children }: { children: React.ReactNode }) => {
  return <Provider theme={theme}>{children}</Provider>;
};
